import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDealersDataView } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import loadDealers from "@insite/client-framework/Store/Data/Dealers/Handlers/LoadDealers";
import { GetDealersApiParameter } from "@insite/client-framework/Services/DealerService";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface Fields {
    heading: string;
    description: string;
    allLabel: string;
    searchRadius: number;
    pageSize: number;
    centerLatitude?: string;
    centerLongitude?: string;
}

// The request parameter is derived from this widget's own CMS fields. We deliberately do NOT use
// state.pages.locationFinder.getDealersParameter: that is shared with the LocationFinder widget, which
// overwrites it and clears the dealers data, so both widgets would fight over the same request.
const buildParameter = (fields: Fields): GetDealersApiParameter => ({
    name: "",
    latitude: Number(fields.centerLatitude) || 0,
    longitude: Number(fields.centerLongitude) || 0,
    radius: fields.searchRadius > 0 ? fields.searchRadius : undefined,
    page: 1,
    pageSize: fields.pageSize > 0 ? fields.pageSize : 100,
});

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps & { fields: Fields }) => ({
    dealersDataView: getDealersDataView(state, buildParameter(ownProps.fields)),
});

const mapDispatchToProps = {
    loadDealers,
};

type Props = WidgetProps & { fields: Fields } & ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface BranchLocationsStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    description?: InjectableCss;
    tabs?: InjectableCss;
    stateRow?: InjectableCss;
    stateHeader?: InjectableCss;
    stateName?: InjectableCss;
    stateCount?: InjectableCss;
    selectedStateTitle?: InjectableCss;
    cardsGrid?: InjectableCss;
    card?: InjectableCss;
    cardTitle?: InjectableCss;
    cardAddress?: InjectableCss;
    cardPhone?: InjectableCss;
    phoneSlot?: InjectableCss;
    directionsBtn?: InjectableCss;
    message?: InjectableCss;
}

const PHONE_BLUE = "#0b8fd8";
const CARD_GREY = "#f2f2f2";
const TAB_ACTIVE_BG = "#e4efe7";

export const branchLocationsStyles: BranchLocationsStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 64px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            font-size: 32px;
            line-height: 40px;
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${COLORS.black};
            margin: 0 0 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-size: 18px;
            line-height: 24px;
            color: ${COLORS.black};
            max-width: 480px;
            margin: 0 0 24px;
        `,
    },
    tabs: {
        css: css`
            display: flex;
            gap: 16px;
            overflow-x: auto;
            padding-bottom: 16px;
            border-bottom: 1px solid #e3e2e2;
            scrollbar-width: none;
            &::-webkit-scrollbar {
                display: none;
            }
            button {
                flex: 0 0 auto;
                padding: 4px 12px;
                border: 0;
                border-radius: 40px;
                background: transparent;
                font-size: 16px;
                line-height: 24px;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                color: ${COLORS.background.tag};
                cursor: pointer;
                white-space: nowrap;
                &:focus-visible {
                    outline: 2px solid ${getColor("primary")};
                    outline-offset: 2px;
                }
            }
            button[aria-selected="true"] {
                background: ${TAB_ACTIVE_BG};
                color: ${getColor("primary")};
            }
        `,
    },
    stateRow: {
        css: css`
            border-bottom: 1px solid #e3e2e2;
            background: ${COLORS.white};
        `,
    },
    stateHeader: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 24px 0;
            background: none;
            border: 0;
            cursor: pointer;
            text-align: left;
            span:first-child {
                font-family: Poppins;
                font-weight: 600;
                font-style: SemiBold;
                font-size: 24px;
                leading-trim: NONE;
                line-height: 32px;
                letter-spacing: 0%;
            }
                span:nth-child(2) {
                font-size: 18px;
                line-height: 24px;
                letter-spacing: 0%;
            }
            &:focus-visible {
                outline: 2px solid ${getColor("primary")};
                outline-offset: 2px;
            }
        `,
    },
    stateName: {
        css: css`
            font-size: 20px;
            line-height: 28px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.black};
        `,
    },
    stateCount: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-style: italic;
            font-weight: 400;
            color: #636161;
            svg {
                width: 16px;
                height: 16px;
            }
        `,
    },
    selectedStateTitle: {
        css: css`
            font-size: 20px;
            line-height: 28px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.black};
            margin: 24px 0 16px;
        `,
    },
    cardsGrid: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, 280px);
            gap: 16px;
            padding: 0 0 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                    `,
                    css`
                        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    card: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 16px;
            background: ${CARD_GREY};
        `,
    },
    cardTitle: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.black};
            margin-bottom: 8px;
        `,
    },
    cardAddress: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            color: #262525;
            margin: 0;
        `,
    },
    phoneSlot: {
        css: css`
            /* keeps the row height even when a branch has no phone number */
            min-height: 20px;
        `,
    },
    cardPhone: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            line-height: 20px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${PHONE_BLUE};
            text-decoration: none;
            svg {
                width: 14px;
                height: 14px;
            }
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    directionsBtn: {
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            margin-top: auto;
            align-self: stretch;
            padding: 6px 16px;
            border: 1px solid ${getColor("secondary")};
            border-radius: 16px;
            background: ${COLORS.white};
            color: ${getColor("secondary")};
            font-size: 12px;
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            text-decoration: none;
            svg {
                width: 12px;
                height: 12px;
            }
            &:hover {
                background: ${getColor("secondary")};
                color: ${getColor("secondary.contrast")};
            }
            &:focus-visible {
                outline: 2px solid ${getColor("secondary")};
                outline-offset: 2px;
            }
        `,
    },
    message: {
        css: css`
            padding: 24px 0;
            font-size: 14px;
            color: #4a4848;
        `,
    },
};

const US_STATES: Record<string, string> = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
    DC: "District of Columbia",
};

const getStateName = (raw?: string) => {
    const value = (raw ?? "").trim();
    return US_STATES[value.toUpperCase()] ?? value;
};

const formatPhone = (raw?: string) => {
    const digits = (raw ?? "").replace(/\D/g, "");
    const d = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (raw ?? "");
};

const getAddress = (dealer: DealerModel) => {
    const d = dealer as any;
    const line1 = [d.address1, d.address2].filter(Boolean).join(", ");
    const line2 = [d.city, [d.state, d.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return { line1, line2 };
};

const getDirectionsUrl = (dealer: DealerModel) => {
    const d = dealer as any;
    const { line1, line2 } = getAddress(dealer);
    const destination =
        d.latitude && d.longitude && (d.latitude !== 0 || d.longitude !== 0)
            ? `${d.latitude},${d.longitude}`
            : [line1, line2].filter(Boolean).join(", ");
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

const PlusIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M8 2v12M2 8h12" />
    </svg>
);
const MinusIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M2 8h12" />
    </svg>
);
const PhoneIcon = () => (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M3.6 1.5c.3-.3.8-.3 1.1 0l1.7 2.1c.2.3.2.7 0 1l-.9 1.1a8.6 8.6 0 0 0 3.8 3.8l1.1-.9c.3-.2.7-.2 1 0l2.1 1.7c.3.3.3.8 0 1.1l-1 1c-.7.7-1.8.9-2.7.5A13.2 13.200 0 0 1 2.1 4.2c-.4-.9-.2-2 .5-2.700l1-1z" />
    </svg>
);
const PinIcon = () => (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1a5 5 0 0 0-5 5c0 3.500 5 9 5 9s5-5.5 5-9a5 5 0 0 0-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
    </svg>
);

const BranchLocations = ({ fields, dealersDataView, loadDealers }: Props) => {
    const styles = branchLocationsStyles;
    const [activeState, setActiveState] = useState<string>("all");
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const errorStatusCode = (dealersDataView as any).errorStatusCode;
    const hasError = !!errorStatusCode;
    const isLoading = !hasError && (!dealersDataView.value || !!dealersDataView.isLoading);

    const parameter = useMemo(
        () => buildParameter(fields),
        [fields.searchRadius, fields.pageSize, fields.centerLatitude, fields.centerLongitude],
    );

    // load when we have no data for our parameter (first render, or if another widget cleared the shared dealers data)
    useEffect(() => {
        if (!dealersDataView.value && !dealersDataView.isLoading && !hasError) {
            loadDealers(parameter);
        }
    }, [parameter, dealersDataView.value, dealersDataView.isLoading, hasError]);

    // group dealers by state, states sorted A-Z
    const grouped = useMemo(() => {
        const map = new Map<string, DealerModel[]>();
        (dealersDataView.value ?? []).forEach(dealer => {
            const key = getStateName((dealer as any).state);
            if (!key) {
                return;
            }
            map.set(key, [...(map.get(key) ?? []), dealer]);
        });
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [dealersDataView.value]);

    const visible = activeState === "all" ? grouped : grouped.filter(([state]) => state === activeState);
    const toggle = (state: string) => setExpanded(prev => ({ ...prev, [state]: !prev[state] }));

    const renderCards = (dealers: DealerModel[], state: string) => (
        <StyledWrapper {...styles.cardsGrid}>
            {dealers.map((dealer, i) => {
                const { line1, line2 } = getAddress(dealer);
                const phone = (dealer as any).phone as string | undefined;
                return (
                    <StyledWrapper key={`${state}-${(dealer as any).id ?? i}`} {...styles.card}>
                        <Typography as="h3" {...styles.cardTitle}>
                            {(dealer as any).name}
                        </Typography>
                        <Typography as="p" {...styles.cardAddress}>
                            {line1}
                            {line1 && line2 ? "," : ""}
                            {line1 && line2 && <br />}
                            {line2}
                        </Typography>
                        <StyledWrapper {...styles.phoneSlot}>
                            {phone && (
                                <StyledWrapper as="a" href={`tel:${phone.replace(/\D/g, "")}`} {...styles.cardPhone}>
                                    <PhoneIcon />
                                    {formatPhone(phone)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                       
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="branchLocations">
            <Typography as="h2" {...styles.heading}>
                {fields.heading}
            </Typography>
            {fields.description && (
                <Typography as="p" {...styles.description}>
                    {fields.description}
                </Typography>
            )}

            <StyledWrapper {...styles.tabs} role="tablist" aria-label={fields.heading}>
                {[["all", fields.allLabel || "All States"], ...grouped.map(([s]) => [s, s])].map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={activeState === value}
                        onClick={() => setActiveState(value)}
                    >
                        {label}
                    </button>
                ))}
            </StyledWrapper>

            {isLoading && (
                <Typography as="p" {...styles.message}>
                    {translate("Loading")}...
                </Typography>
            )}
            {hasError && (
                <Typography as="p" {...styles.message}>
                    {translate("Unable to load locations")} ({errorStatusCode})
                </Typography>
            )}
            {!isLoading && !hasError && grouped.length === 0 && (
                <Typography as="p" {...styles.message}>
                    {translate("No locations found")}
                </Typography>
            )}

            {/* single state selected: title + cards (second screen in the design) */}
            {!isLoading &&
                activeState !== "all" &&
                visible.map(([state, dealers]) => (
                    <React.Fragment key={state}>
                        <Typography as="h3" {...styles.selectedStateTitle}>
                            {state} {translate("Locations")}
                        </Typography>
                        {renderCards(dealers, state)}
                    </React.Fragment>
                ))}

            {/* all states: accordion rows (first screen in the design) */}
            {!isLoading &&
                activeState === "all" &&
                visible.map(([state, dealers]) => {
                    const isOpen = !!expanded[state];
                    return (
                        <StyledWrapper key={state} {...styles.stateRow}>
                            <StyledWrapper
                                as="button"
                                type="button"
                                aria-expanded={isOpen}
                                {...styles.stateHeader}
                                onClick={() => toggle(state)}
                            >
                                <Typography as="span" {...styles.stateName}>
                                    {state}
                                </Typography>
                                <Typography as="span" {...styles.stateCount}>
                                    {dealers.length}{" "}
                                    {dealers.length === 1 ? translate("Location") : translate("Locations")}
                                    {isOpen ? <MinusIcon /> : <PlusIcon />}
                                </Typography>
                            </StyledWrapper>
                            {isOpen && renderCards(dealers, state)}
                        </StyledWrapper>
                    );
                })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BranchLocations),
    definition: {
        group: "Common",
        displayName: "CEN Branch Locations",
        fieldDefinitions: [
            {
                name: "heading",
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "Branch Locations",
                fieldType: "Translatable",
            },
            {
                name: "description",
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "Connect with your local Centro branch for product support, solutions, and service.",
                fieldType: "Translatable",
            },
            {
                name: "allLabel",
                displayName: "'All' tab label",
                editorTemplate: "TextField",
                defaultValue: "All States",
                fieldType: "Translatable",
            },
            {
                name: "searchRadius",
                displayName: "Search radius (0 = API default)",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                min: 0,
                max: 10000,
                fieldType: "General",
            },
            {
                name: "pageSize",
                displayName: "Max branches to load",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                min: 1,
                max: 500,
                fieldType: "General",
            },
            {
                name: "centerLatitude",
                displayName: "Fallback center latitude (used if site default is 0)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "centerLongitude",
                displayName: "Fallback center longitude (used if site default is 0)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
